import { Receipt } from 'lucide-react';
import { useUpcomingPayables, useUpcomingReceivables } from '@/hooks/useFinancialData';
import { formatBRL, formatDate } from '@/lib/format';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';

interface Item {
  id: string;
  payee: string;
  description: string;
  due: string;
  amount_cents: number;
  status: string;
}

function Row({ item, kind }: { item: Item; kind: 'payable' | 'receivable' }) {
  const overdue = item.status === 'overdue';
  return (
    <li className="py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink truncate">{item.payee}</p>
        <p className="text-xs text-ink-muted truncate">{item.description}</p>
      </div>
      <div className="text-right shrink-0">
        <p
          className={cn(
            'tabular-nums text-sm font-semibold',
            kind === 'payable' ? 'text-negative' : 'text-positive'
          )}
        >
          {kind === 'payable' ? '−' : '+'}
          {formatBRL(item.amount_cents)}
        </p>
        <p className={cn('text-xs', overdue ? 'text-negative font-semibold' : 'text-ink-muted')}>
          {overdue ? 'Atrasado · ' : 'Vence em '}
          {formatDate(item.due)}
        </p>
      </div>
    </li>
  );
}

export function UpcomingPayables() {
  const { data, loading } = useUpcomingPayables();
  const total = data.reduce((s, p) => s + p.amount_cents, 0);

  return (
    <div className="card p-5 h-full">
      <SectionHeader
        title="A pagar — próximos 30 dias"
        subtitle={data.length > 0 ? `Total: ${formatBRL(total)}` : 'Nenhum registro'}
        action={<button className="btn-ghost !p-1.5 !text-xs">Ver todos</button>}
      />
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-12 rounded bg-surface-subtle animate-pulse" />)}
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Nenhum pagamento próximo"
          description="Lançamentos de contas a pagar aparecerão aqui."
        />
      ) : (
        <ul className="divide-y divide-border">
          {data.map((p) => <Row key={p.id} item={p} kind="payable" />)}
        </ul>
      )}
    </div>
  );
}

export function UpcomingReceivables() {
  const { data, loading } = useUpcomingReceivables();
  const total = data.reduce((s, p) => s + p.amount_cents, 0);

  return (
    <div className="card p-5 h-full">
      <SectionHeader
        title="A receber — próximos 30 dias"
        subtitle={data.length > 0 ? `Total: ${formatBRL(total)}` : 'Nenhum registro'}
        action={<button className="btn-ghost !p-1.5 !text-xs">Ver todos</button>}
      />
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-12 rounded bg-surface-subtle animate-pulse" />)}
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Nenhum recebimento próximo"
          description="Lançamentos de contas a receber aparecerão aqui."
        />
      ) : (
        <ul className="divide-y divide-border">
          {data.map((r) => <Row key={r.id} item={r} kind="receivable" />)}
        </ul>
      )}
    </div>
  );
}
